import React, { useContext, useEffect, useState } from "react";
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControl,
    Grid,
    IconButton,
    InputAdornment,
    TextField,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/AddCircleOutline";
import { GlobalContext } from "../Utils/Contexts";
import { MobileDateTimePicker } from "@mui/x-date-pickers";
import RemoveCircleOutline from "@mui/icons-material/RemoveCircleOutline";
import AircraftTypeSelector from "../Components/Selects/AircraftTypeSelector";
import CommentSection from "./CommentSection";
import { useMutation, useQuery } from "@apollo/client";
import {
    CREATE_OR_UPDATE_COMMENT,
    CREATE_OR_UPDATE_PILOT_DUTY,
    DELETE_PILOT_DUTY_BY_ID,
    getComments,
    getDuty,
    getDutyTimesSummary,
    getIfrCurrencyByPersonAndDate,
    getNightCurrencyByPersonAndDate,
    getTolCurrencyByPersonAndDate,
} from "../GQL/queries";
import DutyTypeSelector from "../Components/Selects/DutyTypeSelector";
import { Comment, PilotDuty } from "../Generated/avroGraphqlDuty";
import { localToUTC, makeLocalAppearUTC, prune } from "../Utils/UtilityMethods";
import { ValidatePilotDuty } from "../Utils/ValidatePilotDuty";
import { DutyItemValidationResult } from "../Utils/Interfaces";
import { PilotDutyErrorHelper } from "../Utils/PilotDutyErrorHelper";
import FlightTakeoffIcon from "@mui/icons-material/FlightTakeoff";
import FlightLandIcon from "@mui/icons-material/FlightLand";
import WbSunnyIcon from "@mui/icons-material/WbSunny";
import NightlightRoundIcon from "@mui/icons-material/NightlightRound";
import LocationSelector from "../Components/Selects/LocationSelector";
import NightCurrencySection from "./NightCurrencySection";
import IfrCurrencySection from "./IfrCurrencySection";
import TolCurrencySection from "./TolCurrencySection";
import DutyHoursSummarySection from "./DutyHoursSummarySection";
import {
    DutyItemAttrTypesEnum,
    clearCacheForFutureSelectedDates,
    handleAddItemClicked,
    handleRemoveItemClicked,
    handleTimeChange,
    handleTimeEditOnBlur,
    onBlur,
    onChange,
} from "../Utils/PilotDutyUtils";

export interface State {
    HasChanges: boolean;
    PilotDuty: PilotDuty;
    Errors: DutyItemValidationResult[];
    ShowDeleteDialog: boolean;
    LocalId: number;
    Comments: Comment[];
    ExpandedAccordions: number[];
}

export enum AccordionIndexEnum {
    night_currency = 1,
    ifr_currency = 2,
    tol_currency = 3,
    daily_time = 4,
    max_flight_time = 5,
    max_duty_time = 6,
    medivac_max_flight_time = 7,
    medivac_max_duty_time = 8,
    medivac_free_from_duty_time = 9,
}

export function PilotDutyPage(): JSX.Element {
    const context = useContext(GlobalContext);

    const {
        error: getDutyError,
        data: getDutyDbResult,
        loading: isDutyLoading,
    } = useQuery(getDuty, {
        variables: { personId: Number(context.loggedInUser.id), date: context.selectedDate },
        notifyOnNetworkStatusChange: true,
    });

    const {
        error: getNightCurrencyError,
        data: getNightCurrencyDbResult,
        loading: getNightCurrencyIsLoading,
    } = useQuery(getNightCurrencyByPersonAndDate, {
        /*
        skip attribute makes sure to run this only when getDuty query is fetched 
        This is helps to maintain the order of execution of these queries
        */
        skip: isDutyLoading,
        /*
        notifyOnNetworkStatusChange attribute set as true ensures that the loading(getNightCurrencyIsLoading) value is updated correctly when using refetchQueries with mutation(in this case, createOrUpdatePilotDutyMutation)
         */
        notifyOnNetworkStatusChange: true,
        variables: { personId: Number(context.loggedInUser.id), date: context.selectedDate },
    });

    const {
        error: getIfrCurrencyError,
        data: getIfrCurrencyDbResult,
        loading: getIfrCurrencyIsLoading,
    } = useQuery(getIfrCurrencyByPersonAndDate, {
        skip: !getNightCurrencyDbResult,
        notifyOnNetworkStatusChange: true,
        variables: { personId: Number(context.loggedInUser.id), date: context.selectedDate },
    });

    const {
        error: getTolCurrencyError,
        data: getTolCurrencyDbResult,
        loading: getTolCurrencyIsLoading,
    } = useQuery(getTolCurrencyByPersonAndDate, {
        skip: !getIfrCurrencyDbResult,
        notifyOnNetworkStatusChange: true,
        variables: { personId: Number(context.loggedInUser.id), date: context.selectedDate },
    });

    const {
        error: getDutyTimesSummaryError,
        data: getDutyTimesSummaryDbResult,
        loading: getDutyTimesSummaryIsLoading,
    } = useQuery(getDutyTimesSummary, {
        skip: !getTolCurrencyDbResult,
        notifyOnNetworkStatusChange: true,
        variables: { personId: Number(context.loggedInUser.id), date: context.selectedDate },
    });

    const [localState, setLocalState] = useState<State>({
        HasChanges: false,
        PilotDuty: {} as PilotDuty,
        Errors: [] as DutyItemValidationResult[],
        ShowDeleteDialog: false,
        LocalId: -1,
        Comments: [] as Comment[],
        ExpandedAccordions: [],
    });

    const [createOrUpdatePilotDutyMutation, { loading: isCreateOrUpdatePilotDutyMutationLoading }] = useMutation(CREATE_OR_UPDATE_PILOT_DUTY, {
        refetchQueries: [
            getDuty,
            getComments,
            getIfrCurrencyByPersonAndDate,
            getNightCurrencyByPersonAndDate,
            getTolCurrencyByPersonAndDate,
            getDutyTimesSummary,
        ],
        update(cache, { data }) {
            if (data) clearCacheForFutureSelectedDates(cache, context);
        },
        onError(error, clientOptions) {
            console.log(`ON CREATE OR UPDATE DUTY ERROR = ${JSON.stringify(error)}`);
        },
    });

    const [createOrUpdateComment, { loading: isCreateOrUpdateCommentMutationLoading }] = useMutation(CREATE_OR_UPDATE_COMMENT, {
        refetchQueries: [getComments],
        onError(error, clientOptions) {
            console.log(`ON CREATE OR UPDATE COMMENT ERROR = ${JSON.stringify(error)}`);
        },
    });

    const [deletePilotDutyMutation, { loading: isDeletePilotDutyMutationLoading }] = useMutation(DELETE_PILOT_DUTY_BY_ID, {
        onCompleted: (data) => {
            if (data.deleteDutyById) setLocalState({ ...localState, ShowDeleteDialog: false, PilotDuty: null as any as PilotDuty });
        },
        update(cache, { data: { deleteDutyById } }) {
            if (deleteDutyById) {
                cache.writeQuery({
                    query: getDuty,
                    variables: { personId: Number(context.loggedInUser.id), date: context.selectedDate },
                    data: { getDuty: null },
                });
                clearCacheForFutureSelectedDates(cache, context);
            }
        },
        refetchQueries: [
            getDuty,
            getComments,
            getIfrCurrencyByPersonAndDate,
            getNightCurrencyByPersonAndDate,
            getTolCurrencyByPersonAndDate,
            getDutyTimesSummary,
        ],
        onError(error, clientOptions) {
            console.log(`ON DELETE PILOT DUTY ERROR = ${JSON.stringify(error)}`);
        },
    });

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const isFixedWing = process.env.REACT_APP_IS_FIXED_WING ? (process.env.REACT_APP_IS_FIXED_WING === "true" ? true : false) : false;

    useEffect(() => {
        if (isDutyLoading === false && getDutyDbResult) setLocalState({ ...localState, PilotDuty: getDutyDbResult.getDuty });
    }, [isDutyLoading, getDutyDbResult]);

    function onBlurSelects() {
        if (localState.HasChanges) {
            const updatedPilotDuty = localState.PilotDuty;

            const errors = ValidatePilotDuty(localState.PilotDuty);
            setLocalState({ ...localState, Errors: errors });
        }
    }

    const accordionClicked = (index: number) => {
        if (localState.ExpandedAccordions.includes(index))
            setLocalState({ ...localState, ExpandedAccordions: localState.ExpandedAccordions.filter((number) => number !== index) });
        else setLocalState({ ...localState, ExpandedAccordions: [...localState.ExpandedAccordions, index] });
    };

    const collapseAll = () => {
        setLocalState({ ...localState, ExpandedAccordions: [] });
    };

    const expandAll = () => {
        // adding all the 9 sections from the AccordionIndexEnum
        setLocalState({ ...localState, ExpandedAccordions: [1, 2, 3, 4, 5, 6, 7, 8, 9] });
    };

    function handleOnClickSave() {
        if (localState.HasChanges) {
            const updatedPilotDuty = prune(localState.PilotDuty);

            const errors = ValidatePilotDuty(localState.PilotDuty);
            if (errors.length === 0) {
                if (localState.PilotDuty)
                    createOrUpdatePilotDutyMutation({
                        variables: {
                            input: updatedPilotDuty,
                        },
                    });

                if (localState.Comments) {
                    localState.Comments.forEach((comment) => {
                        if (comment.id < 0) {
                            const { __typename, id, ...rest } = comment; // prune the local id and __typename
                            createOrUpdateComment({ variables: { comment: rest } });
                        } else {
                            const { __typename, ...rest } = comment; // prune __typename
                            createOrUpdateComment({ variables: { comment: rest } });
                        }
                    });
                }
                setLocalState({ ...localState, HasChanges: false, Errors: errors });
            } else {
                setLocalState({ ...localState, Errors: errors });
            }
        }
    }

    function handleOnClickDelete() {
        deletePilotDutyMutation({
            variables: {
                deleteDutyByIdId: localState.PilotDuty.id,
            },
        });
    }

    if (getDutyError) return <>Error in fecthing data..</>;
    else {
        return isDutyLoading || isCreateOrUpdateCommentMutationLoading || isCreateOrUpdatePilotDutyMutationLoading || isDeletePilotDutyMutationLoading ? (
            <Grid container m={2} justifyContent="center">
                <CircularProgress />
            </Grid>
        ) : (
            <>
                <FormControl fullWidth>
                    <Dialog open={localState.ShowDeleteDialog} aria-labelledby="alert-dialog-title" aria-describedby="alert-dialog-description">
                        <DialogTitle id="alert-dialog-title">{"Caution!"}</DialogTitle>
                        <DialogContent>
                            <DialogContentText id="alert-dialog-description">
                                Are you sure you wish to delete the pilot duty for the selected date?
                            </DialogContentText>
                        </DialogContent>
                        <DialogActions sx={{ pb: 2, pr: 2 }}>
                            <Button
                                variant="contained"
                                onClick={() => {
                                    setLocalState({ ...localState, ShowDeleteDialog: false });
                                }}>
                                Cancel
                            </Button>
                            <Button variant="contained" color={"error"} onClick={handleOnClickDelete}>
                                Confirm
                            </Button>
                        </DialogActions>
                    </Dialog>
                    <Grid container marginTop={2} justifyContent={"space-between"}>
                        <Grid item xs={5} sm={9} md={10} lg={10.75}>
                            <Typography variant="button" fontSize={20} marginLeft={2} marginTop={2}>
                                PILOT DUTY
                            </Typography>
                        </Grid>
                        <Grid container item xs={7} sm={3} md={2} lg={1.25} justifyContent={"center"}>
                            <Grid item>
                                <Button
                                    disabled={localState.PilotDuty === null || localState.PilotDuty === ({} as PilotDuty)}
                                    sx={{ mr: 1 }}
                                    title="DELETE PILOT DUTY"
                                    variant="contained"
                                    color="error"
                                    onClick={() => {
                                        setLocalState({ ...localState, ShowDeleteDialog: true });
                                    }}>
                                    DELETE
                                </Button>
                            </Grid>
                            <Grid item>
                                <Button disabled={!localState.HasChanges} title="SAVE CHANGES" variant="contained" color="success" onClick={handleOnClickSave}>
                                    SAVE
                                </Button>
                            </Grid>
                        </Grid>
                    </Grid>
                    {localState.PilotDuty &&
                        localState.PilotDuty.DutyItems &&
                        localState.PilotDuty.DutyItems.map((dutyItem, dutyItemIndex) => {
                            return (
                                dutyItem && (
                                    <Grid
                                        container
                                        key={dutyItem.id ?? dutyItemIndex}
                                        sx={{ paddingLeft: 2, paddingTop: 3, paddingRight: 2 }}
                                        mt={isMobile && dutyItemIndex !== 0 ? 4 : 0}>
                                        <Grid item container key={dutyItem.id} spacing={1} xs={11}>
                                            <Grid item xs={12} sm={3} md={2.25} lg={0.75}>
                                                <LocationSelector
                                                    isRequired={false}
                                                    sourceProperty="location_start"
                                                    labelText="START LOCATION"
                                                    initialValue={dutyItem.location_start}
                                                    payloadProperty={"location_start"}
                                                    localState={localState}
                                                    dutyItemId={dutyItem.id}
                                                    setLocalState={setLocalState}
                                                    onBlurSelects={onBlurSelects}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={3} md={2.25} lg={1}>
                                                <MobileDateTimePicker
                                                    value={dutyItem.duty_start ? makeLocalAppearUTC(dutyItem.duty_start) : dutyItem.duty_start}
                                                    label={"START TIME"}
                                                    ampm={false}
                                                    onChange={(value) =>
                                                        handleTimeChange("duty_start", dutyItem.id, localToUTC(value), localState, setLocalState)
                                                    }
                                                    inputFormat="YYYY-MM-DD HH:mm"
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            required
                                                            fullWidth
                                                            onBlur={(e) =>
                                                                handleTimeEditOnBlur("duty_start", dutyItem.id, e.target.value, localState, setLocalState)
                                                            }
                                                            error={PilotDutyErrorHelper.hasErrors(localState.Errors, "duty_start", dutyItem.id)}
                                                            helperText={PilotDutyErrorHelper.getErrorMessage(localState.Errors, "duty_start", dutyItem.id)}
                                                        />
                                                    )}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={3} md={2.25} lg={0.75}>
                                                <LocationSelector
                                                    isRequired={false}
                                                    sourceProperty="location_end"
                                                    labelText="END LOCATION"
                                                    initialValue={dutyItem.location_end}
                                                    payloadProperty={"location_end"}
                                                    localState={localState}
                                                    dutyItemId={dutyItem.id}
                                                    setLocalState={setLocalState}
                                                    onBlurSelects={onBlurSelects}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={3} md={2.25} lg={1}>
                                                <MobileDateTimePicker
                                                    value={dutyItem.duty_end ? makeLocalAppearUTC(dutyItem.duty_end) : dutyItem.duty_end}
                                                    label={"END TIME"}
                                                    ampm={false}
                                                    inputFormat="YYYY-MM-DD HH:mm"
                                                    onChange={(value) =>
                                                        handleTimeChange("duty_end", dutyItem.id, localToUTC(value), localState, setLocalState)
                                                    }
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            fullWidth
                                                            required
                                                            onBlur={(e) =>
                                                                handleTimeEditOnBlur("duty_end", dutyItem.id, e.target.value, localState, setLocalState)
                                                            }
                                                            error={PilotDutyErrorHelper.hasErrors(localState.Errors, "duty_end", dutyItem.id)}
                                                            helperText={PilotDutyErrorHelper.getErrorMessage(localState.Errors, "duty_end", dutyItem.id)}
                                                        />
                                                    )}
                                                />
                                            </Grid>
                                            <Grid item xs={6} sm={1.5} md={1} lg={0.5}>
                                                <TextField
                                                    fullWidth
                                                    label={"FLIGHT TIME"}
                                                    value={dutyItem.flight_time == null ? "" : dutyItem.flight_time}
                                                    inputProps={{ style: { textAlign: "right" } }}
                                                    onChange={(e) => onChange(e, "flight_time", dutyItem.id, dutyItem.flight_time, localState, setLocalState)}
                                                    onBlur={(e) => onBlur(e, "flight_time", dutyItem.id, localState, setLocalState)}
                                                    error={PilotDutyErrorHelper.hasErrors(localState.Errors, "flight_time", dutyItem.id)}
                                                    helperText={PilotDutyErrorHelper.getErrorMessage(localState.Errors, "flight_time", dutyItem.id)}
                                                />
                                            </Grid>
                                            <Grid item xs={6} sm={2.5} md={2} lg={1}>
                                                <AircraftTypeSelector
                                                    isRequired={false}
                                                    sourceProperty="aircraft_type_id"
                                                    labelText="A/C TYPE"
                                                    initialValue={dutyItem.AircraftType?.id}
                                                    payloadProperty={"AircraftType"}
                                                    localState={localState}
                                                    dutyItemId={dutyItem.id}
                                                    setLocalState={setLocalState}
                                                    onBlurSelects={onBlurSelects}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={isFixedWing ? 4 : 12} md={isFixedWing ? 2.8 : 7} lg={isFixedWing ? 1.6 : 4}>
                                                <DutyTypeSelector
                                                    isRequired={false}
                                                    sourceProperty="duty_type_id"
                                                    labelText="DUTY TYPE"
                                                    initialValue={dutyItem.duty_type_id}
                                                    payloadProperty={"DutyType"}
                                                    localState={localState}
                                                    dutyItemId={dutyItem.id}
                                                    setLocalState={setLocalState}
                                                    onBlurSelects={onBlurSelects}
                                                />
                                            </Grid>
                                            {isFixedWing ? (
                                                <>
                                                    <Grid item xs={6} sm={2} md={1.3} lg={0.85}>
                                                        <Tooltip title="TOTAL TAKEOFFS">
                                                            <div>
                                                                {/* // <div> is added else tootip might not show on mobile(the issue/bug is not very consistent!) */}
                                                                <TextField
                                                                    fullWidth
                                                                    label={"TOTAL T/O"}
                                                                    value={
                                                                        (dutyItem.ItemAttributes &&
                                                                            dutyItem.ItemAttributes.find(
                                                                                (item) => item?.duty_attr_type_id === DutyItemAttrTypesEnum["total_takeoffs"]
                                                                            )?.value) ??
                                                                        ""
                                                                    }
                                                                    InputProps={{
                                                                        style: { textAlign: "right" },
                                                                        startAdornment: (
                                                                            <InputAdornment position="start">
                                                                                <WbSunnyIcon />
                                                                                <FlightTakeoffIcon />
                                                                            </InputAdornment>
                                                                        ),
                                                                    }}
                                                                    onChange={(e) =>
                                                                        onChange(
                                                                            e,
                                                                            "total_takeoffs",
                                                                            dutyItem.id,
                                                                            dutyItem.ItemAttributes?.find(
                                                                                (item) => item?.duty_attr_type_id === DutyItemAttrTypesEnum["total_takeoffs"]
                                                                            )?.value ?? "",
                                                                            localState,
                                                                            setLocalState
                                                                        )
                                                                    }
                                                                    onBlur={(e) => onBlur(e, "total_takeoffs", dutyItem.id, localState, setLocalState)}
                                                                />
                                                            </div>
                                                        </Tooltip>
                                                    </Grid>
                                                    <Grid item xs={6} sm={2} md={1.3} lg={0.85}>
                                                        <Tooltip title="TOTAL LANDINGS">
                                                            <div>
                                                                <TextField
                                                                    fullWidth
                                                                    label={"TOTAL LAND"}
                                                                    value={
                                                                        (dutyItem.ItemAttributes &&
                                                                            dutyItem.ItemAttributes?.find(
                                                                                (item) => item?.duty_attr_type_id === DutyItemAttrTypesEnum["total_landings"]
                                                                            )?.value) ??
                                                                        ""
                                                                    }
                                                                    InputProps={{
                                                                        style: { textAlign: "right" },
                                                                        startAdornment: (
                                                                            <InputAdornment position="start">
                                                                                <WbSunnyIcon />
                                                                                <FlightLandIcon />
                                                                            </InputAdornment>
                                                                        ),
                                                                    }}
                                                                    onChange={(e) =>
                                                                        onChange(
                                                                            e,
                                                                            "total_landings",
                                                                            dutyItem.id,
                                                                            dutyItem.ItemAttributes?.find(
                                                                                (item) => item?.duty_attr_type_id === DutyItemAttrTypesEnum["total_landings"]
                                                                            )?.value ?? "",
                                                                            localState,
                                                                            setLocalState
                                                                        )
                                                                    }
                                                                    onBlur={(e) => onBlur(e, "total_landings", dutyItem.id, localState, setLocalState)}
                                                                />
                                                            </div>
                                                        </Tooltip>
                                                    </Grid>
                                                    <Grid item xs={6} sm={2} md={1.3} lg={0.85}>
                                                        <Tooltip title="NIGHT TAKEOFFS">
                                                            <div>
                                                                <TextField
                                                                    fullWidth
                                                                    label={"NIGHT T/O"}
                                                                    value={
                                                                        dutyItem.ItemAttributes?.find(
                                                                            (item) => item?.duty_attr_type_id === DutyItemAttrTypesEnum["night_takeoffs"]
                                                                        )?.value ?? ""
                                                                    }
                                                                    InputProps={{
                                                                        style: { textAlign: "right" },
                                                                        startAdornment: (
                                                                            <InputAdornment position="start">
                                                                                <NightlightRoundIcon />
                                                                                <FlightTakeoffIcon />
                                                                            </InputAdornment>
                                                                        ),
                                                                    }}
                                                                    onChange={(e) =>
                                                                        onChange(
                                                                            e,
                                                                            "night_takeoffs",
                                                                            dutyItem.id,
                                                                            dutyItem.ItemAttributes?.find(
                                                                                (item) => item?.duty_attr_type_id === DutyItemAttrTypesEnum["night_takeoffs"]
                                                                            )?.value,
                                                                            localState,
                                                                            setLocalState
                                                                        )
                                                                    }
                                                                    onBlur={(e) => onBlur(e, "night_takeoffs", dutyItem.id, localState, setLocalState)}
                                                                />
                                                            </div>
                                                        </Tooltip>
                                                    </Grid>
                                                    <Grid item xs={6} sm={2} md={1.3} lg={0.85}>
                                                        <Tooltip title="NIGHT LANDINGS">
                                                            <div>
                                                                <TextField
                                                                    fullWidth
                                                                    label={"NIGHT LAND"}
                                                                    value={
                                                                        dutyItem.ItemAttributes?.find(
                                                                            (item) => item?.duty_attr_type_id === DutyItemAttrTypesEnum["night_landings"]
                                                                        )?.value ?? ""
                                                                    }
                                                                    InputProps={{
                                                                        style: { textAlign: "right" },
                                                                        startAdornment: (
                                                                            <InputAdornment position="start">
                                                                                <NightlightRoundIcon />
                                                                                <FlightLandIcon />
                                                                            </InputAdornment>
                                                                        ),
                                                                    }}
                                                                    onChange={(e) =>
                                                                        onChange(
                                                                            e,
                                                                            "night_landings",
                                                                            dutyItem.id,
                                                                            dutyItem.ItemAttributes?.find(
                                                                                (item) => item?.duty_attr_type_id === DutyItemAttrTypesEnum["night_landings"]
                                                                            )?.value,
                                                                            localState,
                                                                            setLocalState
                                                                        )
                                                                    }
                                                                    onBlur={(e) => onBlur(e, "night_landings", dutyItem.id, localState, setLocalState)}
                                                                />
                                                            </div>
                                                        </Tooltip>
                                                    </Grid>
                                                    <Grid item xs={3} sm={1.5} md={1} lg={0.5}>
                                                        <Tooltip title="IFR APPROACHES">
                                                            <div>
                                                                <TextField
                                                                    fullWidth
                                                                    label={"IFR APPR"}
                                                                    value={
                                                                        dutyItem.ItemAttributes?.find(
                                                                            (item) => item?.duty_attr_type_id === DutyItemAttrTypesEnum["ifr_approaches"]
                                                                        )?.value ?? ""
                                                                    }
                                                                    inputProps={{ style: { textAlign: "right" } }}
                                                                    onChange={(e) =>
                                                                        onChange(
                                                                            e,
                                                                            "ifr_approaches",
                                                                            dutyItem.id,
                                                                            dutyItem.ItemAttributes?.find(
                                                                                (item) => item?.duty_attr_type_id === DutyItemAttrTypesEnum["ifr_approaches"]
                                                                            )?.value,
                                                                            localState,
                                                                            setLocalState
                                                                        )
                                                                    }
                                                                    onBlur={(e) => onBlur(e, "ifr_approaches", dutyItem.id, localState, setLocalState)}
                                                                />
                                                            </div>
                                                        </Tooltip>
                                                    </Grid>
                                                    <Grid item xs={3} sm={1.5} md={1} lg={0.5}>
                                                        <Tooltip title="IFR HOURS">
                                                            <div>
                                                                <TextField
                                                                    fullWidth
                                                                    label={"IFR HOURS"}
                                                                    value={
                                                                        dutyItem.ItemAttributes?.find(
                                                                            (item) => item?.duty_attr_type_id === DutyItemAttrTypesEnum["ifr_hours"]
                                                                        )?.value ?? ""
                                                                    }
                                                                    inputProps={{ style: { textAlign: "right" } }}
                                                                    onChange={(e) =>
                                                                        onChange(
                                                                            e,
                                                                            "ifr_hours",
                                                                            dutyItem.id,
                                                                            dutyItem.ItemAttributes?.find(
                                                                                (item) => item?.duty_attr_type_id === DutyItemAttrTypesEnum["ifr_hours"]
                                                                            )?.value,
                                                                            localState,
                                                                            setLocalState
                                                                        )
                                                                    }
                                                                    onBlur={(e) => onBlur(e, "ifr_hours", dutyItem.id, localState, setLocalState)}
                                                                />
                                                            </div>
                                                        </Tooltip>
                                                    </Grid>
                                                    <Grid item xs={3} sm={1.5} md={1} lg={0.5}>
                                                        <Tooltip title="RNP-AR APPROACHES">
                                                            <div>
                                                                <TextField
                                                                    fullWidth
                                                                    label={"RNP-AR APPR"}
                                                                    value={
                                                                        dutyItem.ItemAttributes?.find(
                                                                            (item) => item?.duty_attr_type_id === DutyItemAttrTypesEnum["rnp_approaches"]
                                                                        )?.value ?? ""
                                                                    }
                                                                    inputProps={{ style: { textAlign: "right" } }}
                                                                    onChange={(e) =>
                                                                        onChange(
                                                                            e,
                                                                            "rnp_approaches",
                                                                            dutyItem.id,
                                                                            dutyItem.ItemAttributes?.find(
                                                                                (item) => item?.duty_attr_type_id === DutyItemAttrTypesEnum["rnp_approaches"]
                                                                            )?.value,
                                                                            localState,
                                                                            setLocalState
                                                                        )
                                                                    }
                                                                    onBlur={(e) => onBlur(e, "rnp_approaches", dutyItem.id, localState, setLocalState)}
                                                                />
                                                            </div>
                                                        </Tooltip>
                                                    </Grid>
                                                    <Grid item xs={3} sm={1.5} md={1} lg={0.5}>
                                                        <Tooltip title="RNP-AR SUCCESS">
                                                            <div>
                                                                <TextField
                                                                    fullWidth
                                                                    label={"RNP-AR SUCCESS"}
                                                                    value={
                                                                        dutyItem.ItemAttributes?.find(
                                                                            (item) => item?.duty_attr_type_id === DutyItemAttrTypesEnum["rnp_success"]
                                                                        )?.value ?? ""
                                                                    }
                                                                    inputProps={{ style: { textAlign: "right" } }}
                                                                    onChange={(e) =>
                                                                        onChange(
                                                                            e,
                                                                            "rnp_success",
                                                                            dutyItem.id,
                                                                            dutyItem.ItemAttributes?.find(
                                                                                (item) => item?.duty_attr_type_id === DutyItemAttrTypesEnum["rnp_success"]
                                                                            )?.value,
                                                                            localState,
                                                                            setLocalState
                                                                        )
                                                                    }
                                                                    onBlur={(e) => onBlur(e, "rnp_success", dutyItem.id, localState, setLocalState)}
                                                                />
                                                            </div>
                                                        </Tooltip>
                                                    </Grid>
                                                </>
                                            ) : null}
                                        </Grid>
                                        <Grid item container justifyContent={"center"} alignItems={"center"} xs={1}>
                                            <IconButton
                                                size={"large"}
                                                edge={"end"}
                                                color="error"
                                                title="DELETE DUTY ITEM"
                                                onClick={() => handleRemoveItemClicked(dutyItemIndex, localState, setLocalState)}>
                                                <RemoveCircleOutline />
                                            </IconButton>
                                        </Grid>
                                    </Grid>
                                )
                            );
                        })}
                    <Typography>
                        <IconButton
                            size="large"
                            color="success"
                            title="ADD DUTY ITEM"
                            sx={{ ml: 2, mr: 2, justifyContent: "flex-start" }}
                            onClick={(e) => handleAddItemClicked(e, localState, setLocalState, context)}>
                            <AddIcon />
                        </IconButton>
                    </Typography>
                </FormControl>
                {localState.PilotDuty && localState.PilotDuty.id ? (
                    <Box mt={2}>
                        <CommentSection dutyId={localState.PilotDuty.id} PilotDutyState={localState} setPilotDutyState={setLocalState} />
                    </Box>
                ) : (
                    <Box mt={2}>
                        <Typography variant="button" fontSize={18} marginLeft={2}>
                            COMMENTS
                        </Typography>
                    </Box>
                )}
                <Box ml={2}>
                    <Button sx={{ mr: 2 }} onClick={expandAll}>
                        Expand All Sections
                    </Button>
                    <Button onClick={collapseAll}>Collapse All Sections</Button>
                </Box>
                {localState.PilotDuty && localState.PilotDuty.id ? (
                    localState.PilotDuty.entry_date === context.selectedDate ? (
                        <>
                            <Box mt={4}>
                                <NightCurrencySection
                                    expanded={localState.ExpandedAccordions.includes(AccordionIndexEnum.night_currency)}
                                    accordianIndexValue={AccordionIndexEnum.night_currency}
                                    accordionClicked={accordionClicked}
                                    error={getNightCurrencyError}
                                    data={getNightCurrencyDbResult}
                                    loading={getNightCurrencyIsLoading}
                                />
                            </Box>
                            <Box mt={4}>
                                <IfrCurrencySection
                                    expanded={localState.ExpandedAccordions.includes(AccordionIndexEnum.ifr_currency)}
                                    accordianIndexValue={AccordionIndexEnum.ifr_currency}
                                    accordionClicked={accordionClicked}
                                    error={getIfrCurrencyError}
                                    data={getIfrCurrencyDbResult}
                                    loading={getIfrCurrencyIsLoading}
                                />
                            </Box>
                            <Box mt={4}>
                                <TolCurrencySection
                                    expanded={localState.ExpandedAccordions.includes(AccordionIndexEnum.tol_currency)}
                                    accordianIndexValue={AccordionIndexEnum.tol_currency}
                                    accordionClicked={accordionClicked}
                                    error={getTolCurrencyError}
                                    data={getTolCurrencyDbResult}
                                    loading={getTolCurrencyIsLoading}
                                />
                            </Box>
                            <Box mt={2} mb={5}>
                                <DutyHoursSummarySection
                                    expandedAccordions={localState.ExpandedAccordions}
                                    accordionClicked={accordionClicked}
                                    error={getDutyTimesSummaryError}
                                    data={getDutyTimesSummaryDbResult}
                                    loading={getDutyTimesSummaryIsLoading}
                                />
                            </Box>
                        </>
                    ) : null
                ) : (
                    <>
                        <Box mt={4}>
                            <NightCurrencySection
                                expanded={localState.ExpandedAccordions.includes(AccordionIndexEnum.night_currency)}
                                accordianIndexValue={AccordionIndexEnum.night_currency}
                                accordionClicked={accordionClicked}
                                error={getNightCurrencyError}
                                data={getNightCurrencyDbResult}
                                loading={getNightCurrencyIsLoading}
                            />
                        </Box>
                        <Box mt={4}>
                            <IfrCurrencySection
                                expanded={localState.ExpandedAccordions.includes(AccordionIndexEnum.ifr_currency)}
                                accordianIndexValue={AccordionIndexEnum.ifr_currency}
                                accordionClicked={accordionClicked}
                                error={getIfrCurrencyError}
                                data={getIfrCurrencyDbResult}
                                loading={getIfrCurrencyIsLoading}
                            />
                        </Box>
                        <Box mt={4}>
                            <TolCurrencySection
                                expanded={localState.ExpandedAccordions.includes(AccordionIndexEnum.tol_currency)}
                                accordianIndexValue={AccordionIndexEnum.tol_currency}
                                accordionClicked={accordionClicked}
                                error={getTolCurrencyError}
                                data={getTolCurrencyDbResult}
                                loading={getTolCurrencyIsLoading}
                            />
                        </Box>
                        <Box mt={2} mb={5}>
                            <DutyHoursSummarySection
                                expandedAccordions={localState.ExpandedAccordions}
                                accordionClicked={accordionClicked}
                                error={getDutyTimesSummaryError}
                                data={getDutyTimesSummaryDbResult}
                                loading={getDutyTimesSummaryIsLoading}
                            />
                        </Box>
                    </>
                )}
            </>
        );
    }
}
